// Shared chart utilities for AnalyticsView pie widgets. Single source of
// truth for the "collapse into a Restantes bucket" pattern used by
// ClientAnalyticsWidgets.component.jsx ("Origen de Conversaciones") and
// MessageAnalyticsWidgets.component.jsx ("Distribución de Mensajes") to
// keep pie-chart labels and legends from overflowing when a breakdown
// returns many rows.

// Neutral color reserved for the aggregated "Restantes" bucket produced by
// collapseToTopN — kept out of the real per-item palette so it never gets
// confused with a legitimate slice.
export const REST_COLOR = "#9CA3AF";

/**
 * Keeps the `n` items with the largest `valueKey` and merges every remaining
 * item into a single "Restantes" bucket. Prevents pie-chart label/legend
 * overflow when a breakdown returns many rows (e.g. one row per Facebook Ad
 * ID, or up to two rows — incoming/outgoing — per active channel) — without
 * this guard a company with many sources/channels renders a slice + label
 * per row and the labels overlap into an unreadable chart.
 *
 * The sum of the dropped items' values is preserved in "Restantes" so the
 * pie's total (and therefore each slice's percentage) stays accurate. When
 * there is exactly one item beyond `n`, the input is returned untouched
 * instead — a bucket that would only ever contain a single real item is
 * never worth hiding a source behind "Restantes".
 *
 * @param {Array<Object>} items
 * @param {string} valueKey   key holding the numeric magnitude to rank by
 * @param {string} labelKey   key holding the display name
 * @param {number} [n=5]      max number of individual slices kept
 * @param {string} [restLabel="Restantes"]
 */
export function collapseToTopN(items, valueKey, labelKey, n = 5, restLabel = "Restantes") {
  if (!items || items.length <= n) return items;
  // A single item beyond the top n would be alone in "Restantes" — show it
  // as its own slice instead of hiding it behind an aggregate bucket.
  if (items.length <= n + 1) return items;

  const sorted = [...items].sort((a, b) => (Number(b[valueKey]) || 0) - (Number(a[valueKey]) || 0));
  const top = sorted.slice(0, n);
  const rest = sorted.slice(n);
  const restValue = rest.reduce((sum, item) => sum + (Number(item[valueKey]) || 0), 0);

  return [...top, { [labelKey]: `${restLabel} (${rest.length})`, [valueKey]: restValue, isRest: true }];
}
